/* eslint react-refresh/only-export-components: "off" */
/**
 * @fileoverview Headless anchored hovercard overlay rendered via portal with robust flip & clamp positioning.
 * @module shared/components/ui/AnchoredHoverCard
 */

import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/shared/lib/utils'

export interface FlipState {
  vertical: 'below' | 'above'
  horizontal: 'right' | 'left'
}

/** Hysteresis buffer - how much extra space needed before flipping back */
const HYSTERESIS = 50

/**
 * Compute a safe position for a hovercard given a viewport, an anchor point and box size.
 * Uses hysteresis to prevent flickering at viewport edges.
 */
export function computeHovercardPosition(
  viewportWidth: number,
  viewportHeight: number,
  anchor: { x: number; y: number },
  width: number,
  height: number,
  margin: number,
  offset: { x: number; y: number },
  prevFlip?: FlipState
): { left: number; top: number; transformOrigin: string; flip: FlipState } {
  const abs = (n: number): number => (n < 0 ? -n : n)
  const ox = offset.x
  const oy = offset.y

  // Calculate positions for both orientations
  const belowTop = Math.round(anchor.y + oy)
  const aboveTop = Math.round(anchor.y - height - abs(oy))
  const rightLeft = Math.round(anchor.x + ox)
  const leftLeft = Math.round(anchor.x - width - abs(ox))

  // Determine vertical flip with hysteresis
  let vertical: 'below' | 'above'
  if (prevFlip) {
    if (prevFlip.vertical === 'below') {
      // Currently below - only flip to above if significantly overflowing
      vertical = belowTop + height > viewportHeight - margin ? 'above' : 'below'
    } else {
      // Currently above - only flip back to below if there's plenty of room (hysteresis)
      vertical = belowTop + height + HYSTERESIS < viewportHeight - margin ? 'below' : 'above'
    }
  } else {
    // Initial placement - flip if would overflow
    vertical = belowTop + height > viewportHeight - margin ? 'above' : 'below'
  }

  // Determine horizontal flip with hysteresis
  let horizontal: 'right' | 'left'
  if (prevFlip) {
    if (prevFlip.horizontal === 'right') {
      horizontal = rightLeft + width > viewportWidth - margin ? 'left' : 'right'
    } else {
      horizontal = rightLeft + width + HYSTERESIS < viewportWidth - margin ? 'right' : 'left'
    }
  } else {
    horizontal = rightLeft + width > viewportWidth - margin ? 'left' : 'right'
  }

  let left = horizontal === 'right' ? rightLeft : leftLeft
  let top = vertical === 'below' ? belowTop : aboveTop

  // Clamp to margins
  left = Math.min(Math.max(margin, left), Math.max(margin, viewportWidth - width - margin))
  top = Math.min(Math.max(margin, top), Math.max(margin, viewportHeight - height - margin))

  const originX = horizontal === 'right' ? 'left' : 'right'
  const originY = vertical === 'below' ? 'top' : 'bottom'

  return { left, top, transformOrigin: `${originX} ${originY}`, flip: { vertical, horizontal } }
}

export interface AnchoredHoverCardProps {
  /** Whether the card is visible */
  active: boolean
  /** Viewport absolute anchor coordinates */
  anchor: { x: number; y: number }
  /** Fixed width in px (default 400) */
  width?: number
  /** Estimated height used for first-pass placement before measurement (default 340) */
  estimatedHeight?: number
  /** Minimum margin from viewport edges (default 8) */
  margin?: number
  /** Offset relative to anchor (default { x: 16, y: -16 }) */
  offset?: { x?: number; y?: number }
  /** When true, pointer events are disabled (default true) */
  inert?: boolean
  /** When true, position changes are animated with spring physics (default true) */
  animatePosition?: boolean
  /** Optional extra class names for the surface */
  className?: string
  /** Content */
  children: ReactNode
}

/**
 * Headless anchored hovercard that positions itself relative to a virtual point.
 * Renders into document.body and never clips the viewport on laptop-sized screens.
 *
 * The component is inert (pointer-events: none) by default so it can be used for chart tooltips safely.
 */
export function AnchoredHoverCard({
  active,
  anchor,
  width = 400,
  estimatedHeight = 340,
  margin = 8,
  offset = { x: 16, y: -16 },
  inert = true,
  animatePosition = true,
  className,
  children,
}: AnchoredHoverCardProps): ReactElement | null {
  const [coords, setCoords] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  })
  const cardRef = useRef<HTMLDivElement | null>(null)
  const flipStateRef = useRef<FlipState | undefined>(undefined)

  // Reset flip state when card closes
  useEffect(() => {
    if (!active) {
      flipStateRef.current = undefined
    }
  }, [active])

  // Calculate position whenever active or anchor changes
  useEffect(() => {
    if (!active) return

    const result = computeHovercardPosition(
      window.innerWidth,
      window.innerHeight,
      anchor,
      width,
      estimatedHeight,
      margin,
      { x: offset.x ?? 0, y: offset.y ?? 0 },
      flipStateRef.current
    )
    flipStateRef.current = result.flip
    setCoords({ left: result.left, top: result.top })
  }, [active, anchor, width, estimatedHeight, margin, offset.x, offset.y])

  // After mount or when dimensions/content change, measure and recompute precisely
  useEffect(() => {
    if (!cardRef.current || !active) return

    const r = cardRef.current.getBoundingClientRect()
    const result = computeHovercardPosition(
      window.innerWidth,
      window.innerHeight,
      anchor,
      width,
      Math.ceil(r.height),
      margin,
      { x: offset.x ?? 0, y: offset.y ?? 0 },
      flipStateRef.current
    )
    flipStateRef.current = result.flip
    setCoords({ left: result.left, top: result.top })
  }, [active, anchor, width, margin, offset.x, offset.y, children])

  // Update on viewport resize while active
  useEffect(() => {
    if (!active) return
    const onResize = (): void => {
      const r = cardRef.current?.getBoundingClientRect()
      const h = r ? Math.ceil(r.height) : estimatedHeight
      const result = computeHovercardPosition(
        window.innerWidth,
        window.innerHeight,
        anchor,
        width,
        h,
        margin,
        { x: offset.x ?? 0, y: offset.y ?? 0 },
        flipStateRef.current
      )
      flipStateRef.current = result.flip
      setCoords({ left: result.left, top: result.top })
    }
    const handler = (): void => {
      void requestAnimationFrame(onResize)
    }
    window.addEventListener('resize', handler, { passive: true })
    return () => window.removeEventListener('resize', handler)
  }, [active, anchor, width, margin, offset.x, offset.y, estimatedHeight])

  return createPortal(
    <AnimatePresence>
      {active && (
        <motion.div
          ref={cardRef}
          role="tooltip"
          aria-hidden={!active}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{
            opacity: 1,
            scale: 1,
            ...(animatePosition && { left: coords.left, top: coords.top }),
          }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{
            opacity: { duration: 0.15, ease: 'easeOut' },
            scale: { duration: 0.15, ease: 'easeOut' },
            ...(animatePosition && {
              left: { type: 'spring', stiffness: 350, damping: 40 },
              top: { type: 'spring', stiffness: 350, damping: 40 },
            }),
          }}
          className={cn(
            'glass-card !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] !p-0 text-foreground shadow-md',
            className
          )}
          data-anchored-hover-card="true"
          style={{
            position: 'fixed',
            zIndex: 10000,
            width,
            pointerEvents: inert ? ('none' as const) : 'auto',
            ...(!animatePosition && { left: coords.left, top: coords.top }),
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
